import { useEffect, useRef, useState, useCallback } from 'react';
import './home.css';
import { Link, useLocation } from 'react-router-dom';
import MobileNav from '../common/MobileNav';


const Home = () => {
    const heroRef = useRef(null);
    const location = useLocation();

    // ==============================================
    // STATE MANAGEMENT - All Features
    // ==============================================

    // Existing States

    const [currentBgImage, setCurrentBgImage] = useState(0);

    // Optimized background images with responsive sizes
    const bgImages = [
        {
            // Desktop (1920px)
            desktop: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1920&h=1080&q=80&fit=crop&auto=format',
            // Tablet (1200px)
            tablet: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1200&h=900&q=75&fit=crop&auto=format',
            // Mobile (800px)
            mobile: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800&h=1200&q=70&fit=crop&auto=format',
            alt: 'AI Technology Background'
        },
        {
            desktop: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1920&h=1080&q=80&fit=crop&auto=format',
            tablet: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=900&q=75&fit=crop&auto=format',
            mobile: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=1200&q=70&fit=crop&auto=format',
            alt: 'Neural Network Background'
        }
    ];

    const heroContent = [
        {
            badge: "Trusted IT Solutions Partner",
            title: "Transforming Ideas Into",
            titleGradient: "Digital Excellence",
            description: "We design, develop, and deploy cutting-edge digital solutions that drive measurable business growth. From startups to enterprises, we're the technology partner you can trust."
        },
        {
            badge: "Innovation & Technology",
            title: "Building the Future of",
            titleGradient: "Digital Innovation",
            description: "Empowering businesses with advanced AI, cloud solutions, and modern web technologies. We turn complex challenges into elegant digital experiences."
        }
    ];



    const [currentHeroContent, setCurrentHeroContent] = useState(0);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [showBackToTop, setShowBackToTop] = useState(false);
    const [isRotating, setIsRotating] = useState(false);
    const [imagesLoaded, setImagesLoaded] = useState({});
    const [statsAnimated, setStatsAnimated] = useState(false);
    const [animatedStats, setAnimatedStats] = useState({
        clients: 0,
        countries: 0,
        retention: 0
    });

    // 1. Dark/Light Mode Toggle
    const [theme, setTheme] = useState('dark');

    // 2. Sticky Navigation
    const [isSticky, setIsSticky] = useState(false);

    // 3. Filter System
    const [activeFilter, setActiveFilter] = useState('All');

    // 4. Toast Notifications
    const [toasts, setToasts] = useState([]);

    // 5. Image Lightbox
    const [lightbox, setLightbox] = useState({ isOpen: false, imageUrl: '', index: 0, images: [] });

    // 8. Newsletter Popup
    const [showNewsletter, setShowNewsletter] = useState(false);
    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [newsletterError, setNewsletterError] = useState('');




    // 10. Loading Animation
    const [loading, setLoading] = useState(true);

    // 7. Form Validation (for newsletter)
    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const [imagesPreloaded, setImagesPreloaded] = useState(false);

    // Helper function to get appropriate image size
    const getResponsiveImage = (imageObj) => {
        const width = window.innerWidth;
        if (width <= 576) return imageObj.mobile;
        if (width <= 1024) return imageObj.tablet;
        return imageObj.desktop;
    };

    // ==============================================
    // FEATURE 1: DARK/LIGHT MODE TOGGLE
    // ==============================================

    useEffect(() => {
        const savedTheme = localStorage.getItem('theme') || 'dark';
        setTheme(savedTheme);
        document.documentElement.setAttribute('data-theme', savedTheme);
    }, []);

    const toggleTheme = () => {
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        setTheme(newTheme);
        localStorage.setItem('theme', newTheme);
        document.documentElement.setAttribute('data-theme', newTheme);
        showToast(`Switched to ${newTheme} mode`, 'success');
    };

    // ==============================================
    // FEATURE 2: STICKY NAVIGATION
    // ==============================================

    useEffect(() => {
        const handleScroll = () => {
            setIsSticky(window.scrollY > 100);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // ==============================================
    // FEATURE 4: TOAST NOTIFICATIONS
    // ==============================================

    const showToast = useCallback((message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 5000);
    }, []);

    // ==============================================
    // FEATURE 5: IMAGE LIGHTBOX
    // ==============================================

    const openLightbox = useCallback((imageUrl, index, allImages) => {
        setLightbox({ isOpen: true, imageUrl, index, images: allImages });
        document.body.style.overflow = 'hidden';
    }, []);

    const closeLightbox = useCallback(() => {
        setLightbox({ isOpen: false, imageUrl: '', index: 0, images: [] });
        document.body.style.overflow = 'auto';
    }, []);

    const navigateLightbox = useCallback((direction) => {
        setLightbox(prev => {
            const newIndex = direction === 'next'
                ? (prev.index + 1) % prev.images.length
                : (prev.index - 1 + prev.images.length) % prev.images.length;
            return { ...prev, index: newIndex, imageUrl: prev.images[newIndex] };
        });
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!lightbox.isOpen) return;
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowRight') navigateLightbox('next');
            if (e.key === 'ArrowLeft') navigateLightbox('prev');
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lightbox.isOpen, closeLightbox, navigateLightbox]);



    const handleNewsletterSubmit = (e) => {
        e.preventDefault();
        if (!validateEmail(newsletterEmail)) {
            setNewsletterError('Please enter a valid email address');
            return;
        }
        showToast('Successfully subscribed to newsletter!', 'success');
        setShowNewsletter(false);
        setNewsletterEmail('');
        setNewsletterError('');
    };

    const handleDismissNewsletter = (dontShowAgain) => {
        if (dontShowAgain) {
            localStorage.setItem('newsletter-dismissed', 'true');
        }
        setShowNewsletter(false);
    };

    // ==============================================
    // FEATURE 10: LOADING ANIMATION
    // ==============================================

    useEffect(() => {
        const timer = setTimeout(() => setLoading(false), 1500);
        return () => clearTimeout(timer);
    }, []);

    // ==============================================
    // EXISTING FUNCTIONALITY
    // ==============================================

    useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
        if (!ticking) {
            window.requestAnimationFrame(() => {
                const scrollTop = window.pageYOffset;
                const windowHeight = window.innerHeight;
                const documentHeight = document.documentElement.scrollHeight;
                
                // Removed heroRef parallax entirely - it was broken and blocking mobile
                
                const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
                setScrollProgress(progress);
                setShowBackToTop(scrollTop > 300);

                ticking = false;
            });
            ticking = true;
        }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
}, []);

    // ==============================================
    // FEATURE 6: STAGGER ANIMATIONS
    // ==============================================



    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Get all visible stagger items
                        const visibleItems = Array.from(
                            document.querySelectorAll('.stagger-item')
                        ).filter(item => {
                            const rect = item.getBoundingClientRect();
                            return rect.top < window.innerHeight && rect.bottom > 0;
                        });

                        // Add stagger effect to each visible item
                        visibleItems.forEach((item, index) => {
                            setTimeout(() => {
                                item.classList.add('stagger-in');
                            }, index * 100);
                        });
                    }
                });
            },
            { threshold: 0.1, rootMargin: '50px' }  // ✅ Lower threshold, added margin
        );

        const staggerItems = document.querySelectorAll('.stagger-item');
        staggerItems.forEach(item => observer.observe(item));

        return () => observer.disconnect();
    }, [activeFilter]);

    // Stats counter animation
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !statsAnimated) {
                        setStatsAnimated(true);

                        let clientsCount = 0;
                        const clientsInterval = setInterval(() => {
                            clientsCount += 2;
                            if (clientsCount >= 50) {
                                clientsCount = 50;
                                clearInterval(clientsInterval);
                            }
                            setAnimatedStats(prev => ({ ...prev, clients: clientsCount }));
                        }, 30);

                        let countriesCount = 0;
                        const countriesInterval = setInterval(() => {
                            countriesCount += 1;
                            if (countriesCount >= 15) {
                                countriesCount = 15;
                                clearInterval(countriesInterval);
                            }
                            setAnimatedStats(prev => ({ ...prev, countries: countriesCount }));
                        }, 60);

                        let retentionCount = 0;
                        const retentionInterval = setInterval(() => {
                            retentionCount += 3;
                            if (retentionCount >= 98) {
                                retentionCount = 98;
                                clearInterval(retentionInterval);
                            }
                            setAnimatedStats(prev => ({ ...prev, retention: retentionCount }));
                        }, 25);
                    }
                });
            },
            { threshold: 0.5 }
        );

        const statsSection = document.querySelector('.clients-stats');
        if (statsSection) observer.observe(statsSection);

        return () => observer.disconnect();
    }, [statsAnimated]);

    // Mouse move effect for cards
    const handleMouseMove = useCallback((e) => {
        const card = e.currentTarget;
        const rect = card.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        card.style.setProperty('--mouse-x', `${x}%`);
        card.style.setProperty('--mouse-y', `${y}%`);
    }, []);

    // Image lazy load handler
    const handleImageLoad = useCallback((index) => {
        setImagesLoaded(prev => ({ ...prev, [index]: true }));
    }, []);

    // Scroll to top
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };


    const services = [
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="16 18 22 12 16 6" />
                    <polyline points="8 6 2 12 8 18" />
                </svg>
            ),
            title: 'Web Design & Development',
            description: 'Creating pixel-perfect, responsive experiences that work flawlessly across all devices. From concept to deployment.',
            tag: 'Design',
            category: 'Web Apps'
        },
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
            ),
            title: 'Enterprise Applications',
            description: 'Scalable, secure web applications built with cutting-edge technologies for complex business requirements.',
            tag: 'Development',
            category: 'Enterprise'
        },
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5z" />
                    <path d="M2 17l10 5 10-5" />
                    <path d="M2 12l10 5 10-5" />
                </svg>
            ),
            title: 'Digital Transformation',
            description: 'Strategic technology consulting to modernize operations, optimize processes, and accelerate growth.',
            tag: 'Strategy',
            category: 'Enterprise'
        },
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
            ),
            title: 'Security & Compliance',
            description: 'Enterprise-grade security implementations, vulnerability assessments, and compliance management.',
            tag: 'Security',
            category: 'Enterprise'
        },
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
            ),
            title: 'Growth Marketing',
            description: 'Data-driven digital marketing strategies that deliver measurable results and sustainable growth.',
            tag: 'Marketing',
            category: 'Design'
        },
        {
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
            ),
            title: 'Product Engineering',
            description: 'Full-cycle product development from ideation through launch, with ongoing optimization and support.',
            tag: 'Product',
            category: 'Web Apps'
        }
    ];

    const allProjects = [
        {
            title: 'Enterprise Analytics Platform',
            category: 'Web Apps',
            image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800',
            tags: ['React', 'D3.js', 'Node.js']
        },
        {
            title: 'Healthcare Portal',
            category: 'Enterprise',
            image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800',
            tags: ['HIPAA', 'Cloud', 'API']
        },
        {
            title: 'FinTech Mobile App',
            category: 'Mobile',
            image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800',
            tags: ['iOS', 'Android', 'Security']
        },
        {
            title: 'E-Commerce Redesign',
            category: 'Design',
            image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800',
            tags: ['Shopify', 'CRO', 'A/B Testing']
        }
    ];

    // ==============================================
    // FEATURE 3: FILTER SYSTEM
    // ==============================================

    const categories = ['All', 'Web Apps', 'Mobile', 'Design', 'Enterprise'];

    const filteredProjects = allProjects.filter(project =>
        activeFilter === 'All' || project.category === activeFilter
    );

    const getCategoryCount = (category) => {
        if (category === 'All') return allProjects.length;
        return allProjects.filter(p => p.category === category).length;
    };

useEffect(() => {
    const preloadImages = async () => {
        const imagePromises = bgImages.map((imgObj) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.src = getResponsiveImage(imgObj);
                img.onload = resolve;
                img.onerror = reject;
            });
        });

        try {
            await Promise.all(imagePromises);
            setImagesPreloaded(true);
        } catch (error) {
            console.error('Error preloading images:', error);
            setImagesPreloaded(true); // Continue anyway
        }
    };

    preloadImages();
}, []); // ← Empty dependency array - runs once on mount

// 2. Background and content rotation - only starts after images preloaded
useEffect(() => {
    if (!imagesPreloaded) return;

    const interval = setInterval(() => {
        setCurrentBgImage((prev) => (prev + 1) % bgImages.length);
        setCurrentHeroContent((prev) => (prev + 1) % heroContent.length);
    }, 5000); // 5 seconds

    return () => clearInterval(interval);
}, [imagesPreloaded, bgImages.length, heroContent.length]);


    // ==============================================
    // LOADING SCREEN
    // ==============================================

    if (loading) {
        return (
            <div className="loading-screen">
                <div className="loading-content">
                    <div className="loading-spinner">
                        <div className="spinner-circle"></div>
                        <div className="spinner-logo">E</div>
                    </div>
                    <h2 className="loading-text">Elexyt Technologies</h2>
                </div>
            </div>
        );
    }

    // ==============================================
    // MAIN RENDER
    // ==============================================

    return (
        <>
            {/* Scroll Progress Bar */}
            <div className="scroll-progress" style={{ width: `${scrollProgress}%` }} />

            {/* Toast Notifications */}
            <div className="toast-container">
                {toasts.map(toast => (
                    <div key={toast.id} className={`toast toast-${toast.type}`}>
                        <div className="toast-icon">
                            {toast.type === 'success' && '✓'}
                            {toast.type === 'error' && '✕'}
                            {toast.type === 'info' && 'ℹ'}
                        </div>
                        <span className="toast-message">{toast.message}</span>
                        <button
                            className="toast-close"
                            onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                            aria-label="Close notification"
                        >
                            ✕
                        </button>
                    </div>
                ))}
            </div>

            {/* Theme Toggle Button (in navigation area) */}
            <button
                className="theme-toggle"
                onClick={toggleTheme}
                aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
                {theme === 'dark' ? '☀️' : '🌙'}
            </button>

            {/* Image Lightbox */}
            {lightbox.isOpen && (
                <div className="lightbox-overlay" onClick={closeLightbox}>
                    <button className="lightbox-close" onClick={closeLightbox} aria-label="Close lightbox">
                        ✕
                    </button>
                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <img src={lightbox.imageUrl} alt="Project preview" className="lightbox-image" />
                        {lightbox.images.length > 1 && (
                            <>
                                <button
                                    className="lightbox-nav lightbox-prev"
                                    onClick={() => navigateLightbox('prev')}
                                    aria-label="Previous image"
                                >
                                    ‹
                                </button>
                                <button
                                    className="lightbox-nav lightbox-next"
                                    onClick={() => navigateLightbox('next')}
                                    aria-label="Next image"
                                >
                                    ›
                                </button>
                            </>
                        )}
                        <div className="lightbox-caption">
                            {lightbox.index + 1} / {lightbox.images.length}
                        </div>
                    </div>
                </div>
            )}

            {/* Newsletter Popup */}
            {showNewsletter && (
                <div className="newsletter-overlay" onClick={() => handleDismissNewsletter(false)}>
                    <div className="newsletter-modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="newsletter-close"
                            onClick={() => handleDismissNewsletter(false)}
                            aria-label="Close newsletter popup"
                        >
                            ✕
                        </button>
                        <h3>Stay Updated!</h3>
                        <p>Subscribe to our newsletter for the latest updates and insights.</p>
                        <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
                            <input
                                type="email"
                                placeholder="Enter your email"
                                value={newsletterEmail}
                                onChange={(e) => {
                                    setNewsletterEmail(e.target.value);
                                    setNewsletterError('');
                                }}
                                className={newsletterError ? 'error' : ''}
                                aria-label="Email address"
                            />
                            {newsletterError && <span className="error-message">{newsletterError}</span>}
                            <button type="submit" className="btn btn-primary">Subscribe</button>
                        </form>
                        <label className="newsletter-checkbox">
                            <input
                                type="checkbox"
                                onChange={(e) => handleDismissNewsletter(e.target.checked)}
                            />
                            <span>Don't show this again</span>
                        </label>
                    </div>
                </div>
            )}

            <div className="home" style={{position: 'relative', zIndex: 0}}>
                <section className="hero-carousel">
    {/* Background Images */}
    <div className="hero-background-slider">
        {bgImages.map((imgObj, index) => (
            <div
                key={index}
                className={`hero-bg-image ${currentBgImage === index ? 'active' : ''} ${!imagesPreloaded ? 'loading' : ''}`}
                style={{
                    backgroundImage: imagesPreloaded ? `url(${getResponsiveImage(imgObj)})` : 'none'
                }}
                role="img"
                aria-label={imgObj.alt}
            />
        ))}
        <div className="hero-bg-overlay"></div>
    </div>

    {/* Content Slides */}
    <div className="hero-slides-container">
        {heroContent.map((content, index) => (
            <div
                key={index}
                className={`hero-slide ${currentHeroContent === index ? 'active' : ''}`}
            >
                <div className="container">
                    <div className="hero-content-wrapper">
                        <div className="hero-content">
                            <div className="hero-badge-wrapper">
                                <span className="hero-badge">
                                    <span className="badge-dot"></span>
                                    {content.badge}
                                </span>
                            </div>

                            <h1 className="hero-title">
                                {content.title}
                                <span className="hero-title-gradient"> {content.titleGradient}</span>
                            </h1>

                            <p className="hero-description">
                                {content.description}
                            </p>

                            <div className="hero-actions">
                                <Link to="/contact" className="btn btn-primary btn-large">
                                    Start Your Project
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                        <polyline points="12 5 19 12 12 19" />
                                    </svg>
                                </Link>

                                <Link to="/project" className="btn btn-outline-light btn-large">
                                    View Our Work
                                </Link>
                            </div>
                        </div>

                        {/* Tech Stack Showcase */}
                        <div className="hero-visual">
                            <div className="tech-stack-showcase">
                                <div className="tech-orbit">
                                    <div className="tech-item tech-1">
                                        <span className="tech-icon">⚛️</span>
                                        <span className="tech-label">React</span>
                                    </div>
                                    <div className="tech-item tech-2">
                                        <span className="tech-icon">🎨</span>
                                        <span className="tech-label">Design</span>
                                    </div>
                                    <div className="tech-item tech-3">
                                        <span className="tech-icon">⚡</span>
                                        <span className="tech-label">Fast</span>
                                    </div>
                                    <div className="tech-item tech-4">
                                        <span className="tech-icon">🔒</span>
                                        <span className="tech-label">Secure</span>
                                    </div>
                                    <div className="tech-item tech-5">
                                        <span className="tech-icon">📱</span>
                                        <span className="tech-label">Mobile</span>
                                    </div>
                                    <div className="tech-item tech-6">
                                        <span className="tech-icon">☁️</span>
                                        <span className="tech-label">Cloud</span>
                                    </div>
                                </div>

                                <div className="tech-center">
                                    <div className="tech-pulse"></div>
                                    <div className="tech-logo">E</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        ))}
    </div>
</section>

<section className="clients-section" style={{pointerEvents: 'all', position: 'relative', zIndex: 1}}>
                    <div className="container">
                        <div className="section-header centered">
                            <div className="section-label">
                                <span className="label-line"></span>
                                <span>Trusted Partnerships</span>
                                <span className="label-line"></span>
                            </div>
                            <h2 className="section-title-large">
                                Companies That Trust Our Solutions
                            </h2>
                            <p className="section-subtitle">
                                Powering innovation for industry leaders across the globe
                            </p>
                        </div>

                        <div className="clients-marquee">
                            <div className="clients-track">
                                <div className="client-logo-wrapper">
                                    <img src="/drdo.png" alt="Client 1" className="client-logo" />
                                </div>
                                <div className="client-logo-wrapper">
                                    <img src="/vts.jpg" alt="Client 2" className="client-logo" />
                                </div>
                                <div className="client-logo-wrapper">
                                    <img src="/unigold.png" alt="Client 3" className="client-logo" />
                                </div>
                                <div className="client-logo-wrapper">
                                    <img src="/gbtc.jpg" alt="Client 4" className="client-logo" />
                                </div>
                                <div className="client-logo-wrapper">
                                    <img src="/lrde.jpg" alt="Client 5" className="client-logo" />
                                </div>
                                

                                <div className="client-logo-wrapper">
                                    <img src="/drdo.png" alt="Client 1" className="client-logo" />
                                </div>
                                <div className="client-logo-wrapper">
                                    <img src="/vts.jpg" alt="Client 2" className="client-logo" />
                                </div>
                                <div className="client-logo-wrapper">
                                    <img src="/unigold.png" alt="Client 3" className="client-logo" />
                                </div>
                                <div className="client-logo-wrapper">
                                    <img src="/gbtc.jpg" alt="Client 4" className="client-logo" />
                                </div>
                                <div className="client-logo-wrapper">
                                    <img src="/gbtc.jpg" alt="Client 5" className="client-logo" />
                                </div>
                                
                            </div>
                        </div>

                        {/* <div className="clients-stats">
                            <div className="client-stat-item">
                                <div className="client-stat-value">{animatedStats.clients}+</div>
                                <div className="client-stat-label">Global Clients</div>
                            </div>
                            <div className="client-stat-divider"></div>
                            <div className="client-stat-item">
                                <div className="client-stat-value">{animatedStats.countries}+</div>
                                <div className="client-stat-label">Countries Served</div>
                            </div>
                            <div className="client-stat-divider"></div>
                            <div className="client-stat-item">
                                <div className="client-stat-value">{animatedStats.retention}%</div>
                                <div className="client-stat-label">Client Retention</div>
                            </div>
                        </div> */}
                    </div>
                </section>

                <section className="services-section" style={{pointerEvents: 'all', position: 'relative', zIndex: 1}}>
                    <div className="container">
                        <div className="section-header">
                            <div className="section-label">
                                <span className="label-line"></span>
                                <span>What We Do</span>
                            </div>
                            <h2 className="section-title-large">
                                Comprehensive Digital Services
                            </h2>
                            <p className="section-subtitle">
                                End-to-end solutions designed to accelerate your digital transformation journey
                            </p>
                        </div>

                        <div className="services-grid">
                            {services.map((service, index) => (
                                <div key={index} className="service-card stagger-item" onMouseMove={handleMouseMove}>
                                    <div className="service-card-inner">
                                        <div className="service-header">
                                            <div className="service-icon-wrapper">
                                                <div className="service-icon">{service.icon}</div>
                                            </div>
                                            <span className="service-tag">{service.tag}</span>
                                        </div>

                                        <h3 className="service-title">{service.title}</h3>
                                        <p className="service-description">{service.description}</p>

                                        <Link to="/service" className="service-link">
                                            <span>Explore Service</span>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="5" y1="12" x2="19" y2="12" />
                                                <polyline points="12 5 19 12 12 19" />
                                            </svg>
                                        </Link>
                                    </div>
                                    <div className="service-card-glow"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="projects-section" style={{pointerEvents: 'all', position: 'relative', zIndex: 1}}>
                    <div className="container">
                        <div className="section-header centered">
                            <div className="section-label">
                                <span className="label-line"></span>
                                <span>Featured Work</span>
                                <span className="label-line"></span>
                            </div>
                            <h2 className="section-title-large">
                                Projects That Made an Impact
                            </h2>
                        </div>

                        {/* Filter Buttons */}
                        <div className="filter-container">
                            <div className="filter-buttons">
                                {categories.map(category => (
                                    <button
                                        key={category}
                                        className={`filter-btn ${activeFilter === category ? 'active' : ''}`}
                                        onClick={() => setActiveFilter(category)}
                                    >
                                        {category}
                                        
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="projects-grid">
                            {filteredProjects.map((project, index) => (
                                <div key={index} className="project-card stagger-item">
                                    <div
                                        className="project-image-wrapper"
                                        onClick={() => openLightbox(project.image, index, filteredProjects.map(p => p.image))}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {!imagesLoaded[index] && (
                                            <div className="image-skeleton">
                                                <div className="skeleton-shimmer"></div>
                                            </div>
                                        )}
                                        <img
                                            src={project.image}
                                            alt={project.title}
                                            loading="lazy"
                                            onLoad={() => handleImageLoad(index)}
                                            style={{
                                                opacity: imagesLoaded[index] ? 1 : 0,
                                                transition: 'opacity 0.3s ease'
                                            }}
                                        />
                                        <div className="project-overlay">
                                            <div className="project-tags">
                                                {project.tags.map((tag, tagIndex) => (
                                                    <span key={tagIndex} className="project-tag">{tag}</span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="project-content">
                                        <span className="project-category">{project.category}</span>
                                        <h3 className="project-title">{project.title}</h3>
                                        <Link to="/project" className="project-link">
                                            View Case Study
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="7" y1="17" x2="17" y2="7" />
                                                <polyline points="7 7 17 7 17 17" />
                                            </svg>
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="projects-cta">
                            <Link to="/project" className="btn btn-outline">
                                View All Projects
                            </Link>
                        </div>
                    </div>
                </section>

                <section className="cta-section" style={{pointerEvents: 'all', position: 'relative', zIndex: 1}}>
                    <div className="cta-pattern"></div>
                    <div className="container">
                        <div className="cta-content">
                            <h2 className="cta-title">
                                Ready to Build Something Exceptional?
                            </h2>
                            <p className="cta-description">
                                Let's discuss how we can help transform your vision into reality. Our team is ready to bring your next project to life.
                            </p>
                            <div className="cta-actions">
                                <Link to="/contact" className="btn btn-light btn-large">
                                    Start a Conversation
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                        <polyline points="12 5 19 12 12 19" />
                                    </svg>
                                </Link>
                                <Link to="/service" className="btn btn-outline-light-alt btn-large">
                                    Explore Services
                                </Link>
                            </div>
                            <p className="cta-note">
                                Your information is secure. We never share your data.
                            </p>
                        </div>
                    </div>
                </section>
            </div>

            <button
                className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
                onClick={scrollToTop}
                aria-label="Scroll to top"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="18 15 12 9 6 15" />
                </svg>
            </button>
            <MobileNav />
        </>
    );
};

export default Home;